import { useEffect, useState, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight, X, Play, ImageIcon, Video as VideoIcon, Maximize2 } from 'lucide-react';
import { resolveMediaUrl, getEmbedUrl } from '@/lib/api';

/**
 * Premium media gallery supporting images, uploaded videos and external (YouTube/Vimeo) links.
 *
 * Props:
 *   images: string[]
 *   videos: string[]            // uploaded
 *   videoUrls: string[]         // external (youtube/vimeo)
 *   autoSlideInterval: number   // ms (default 5000)
 */
export const MediaGallery = ({ images = [], videos = [], videoUrls = [], autoSlideInterval = 5000 }) => {
  const items = [
    ...images.filter(Boolean).map((src) => ({ type: 'image', src })),
    ...videos.filter(Boolean).map((src) => ({ type: 'video', src })),
    ...videoUrls.filter(Boolean).map((src) => {
      const embed = getEmbedUrl(src);
      return embed ? { type: 'embed', src: embed, original: src } : null;
    }).filter(Boolean),
  ];

  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [paused, setPaused] = useState(false);
  const intervalRef = useRef(null);

  const next = useCallback(() => setActive((i) => (i + 1) % items.length), [items.length]);
  const prev = useCallback(() => setActive((i) => (i - 1 + items.length) % items.length), [items.length]);

  useEffect(() => {
    if (paused || lightbox || items.length <= 1) return;
    // Don't autoslide while a video/embed is on screen
    const cur = items[active];
    if (cur && (cur.type === 'video' || cur.type === 'embed')) return;
    intervalRef.current = setInterval(next, autoSlideInterval);
    return () => clearInterval(intervalRef.current);
  }, [active, paused, lightbox, items, next, autoSlideInterval]);

  // Touch swipe
  const touchRef = useRef({ startX: 0, dx: 0 });
  const onTouchStart = (e) => { touchRef.current.startX = e.touches[0].clientX; touchRef.current.dx = 0; };
  const onTouchMove = (e) => { touchRef.current.dx = e.touches[0].clientX - touchRef.current.startX; };
  const onTouchEnd = () => {
    if (Math.abs(touchRef.current.dx) > 60) {
      touchRef.current.dx > 0 ? prev() : next();
    }
  };

  // Keyboard nav in lightbox
  useEffect(() => {
    if (!lightbox) return;
    const handler = (e) => {
      if (e.key === 'Escape') setLightbox(false);
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightbox, next, prev]);

  if (!items.length) {
    return (
      <div className="aspect-[4/3] rounded-2xl bg-gray-100 flex items-center justify-center">
        <ImageIcon className="h-16 w-16 text-gray-300" />
      </div>
    );
  }

  const current = items[active];

  const renderItem = (item, opts = {}) => {
    const cls = opts.cls || 'w-full h-full object-cover';
    if (item.type === 'image') {
      return <img src={resolveMediaUrl(item.src)} alt="" className={cls} loading="lazy" />;
    }
    if (item.type === 'video') {
      return (
        <video src={resolveMediaUrl(item.src)} className={cls} controls preload="metadata" playsInline>
          Your browser does not support video playback.
        </video>
      );
    }
    return (
      <iframe src={item.src} className="w-full h-full" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen title="Video" />
    );
  };

  return (
    <div className="space-y-3" data-testid="media-gallery">
      {/* Main viewer */}
      <div
        className="relative rounded-2xl overflow-hidden bg-gray-900 aspect-[4/3] group"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {renderItem(current)}

        {items.length > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous"
              onClick={prev}
              className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/85 backdrop-blur rounded-full flex items-center justify-center hover:bg-white transition-colors shadow"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              aria-label="Next"
              onClick={next}
              className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/85 backdrop-blur rounded-full flex items-center justify-center hover:bg-white transition-colors shadow"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}

        {current.type === 'image' && (
          <button
            type="button"
            onClick={() => setLightbox(true)}
            className="absolute bottom-3 right-3 bg-black/60 hover:bg-black/80 text-white rounded-full p-2 backdrop-blur opacity-0 group-hover:opacity-100 transition-opacity"
            aria-label="Open full screen"
          >
            <Maximize2 className="h-4 w-4" />
          </button>
        )}

        <div className="absolute top-3 right-3 bg-black/60 text-white text-xs px-3 py-1 rounded-full backdrop-blur">
          {active + 1} / {items.length}
        </div>
      </div>

      {/* Thumbnails */}
      {items.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {items.map((item, idx) => (
            <button
              type="button"
              key={idx}
              onClick={() => setActive(idx)}
              className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 relative bg-gray-900 ${
                active === idx ? 'border-brand-orange' : 'border-transparent opacity-70 hover:opacity-100'
              }`}
              aria-label={`Show ${item.type} ${idx + 1}`}
            >
              {item.type === 'image' ? (
                <img src={resolveMediaUrl(item.src)} alt="" className="w-full h-full object-cover" />
              ) : item.type === 'video' ? (
                <>
                  <video src={resolveMediaUrl(item.src)} className="w-full h-full object-cover" muted />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                    <Play className="h-5 w-5 text-white" fill="white" />
                  </div>
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white">
                  <VideoIcon className="h-5 w-5" />
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && current.type === 'image' && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightbox(false)}
        >
          <button
            type="button"
            className="absolute top-4 right-4 text-white/80 hover:text-white"
            onClick={() => setLightbox(false)}
            aria-label="Close"
          >
            <X className="h-8 w-8" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 text-white/80 hover:text-white"
            aria-label="Previous"
          >
            <ChevronLeft className="h-10 w-10" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 text-white/80 hover:text-white"
            aria-label="Next"
          >
            <ChevronRight className="h-10 w-10" />
          </button>
          <img
            src={resolveMediaUrl(current.src)}
            alt=""
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};
